import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { money } from "@/lib/format";
import { HelpHint } from "@/components/HelpHint";

// Fetch the miscellaneous add-on options for a module (and optional LF subtype).
export function useMiscOptions(module, subtype) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    const params = { module };
    if (subtype) params.subtype = subtype;
    api.get("/misc-materials", { params }).then((r) => setOpts(r.data || [])).catch(() => setOpts([]));
  }, [module, subtype]);
  return opts;
}

// Inner editor: rows of [item][qty]. Reused per-piece (Large Format) and per-quote (other modules).
export function MiscRows({ opts, value = [], onChange }) {
  const setRow = (i, k, v) => onChange(value.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => onChange([...(value || []), { material_id: "", qty: 1 }]);
  const rmRow = (i) => onChange(value.filter((_, idx) => idx !== i));
  const opt = (id) => opts.find((o) => o.id === id);
  return (
    <div className="space-y-2" data-testid="misc-rows">
      {(value || []).map((r, i) => {
        const o = opt(r.material_id);
        const line = o ? o.sell_price * (Number(r.qty) || 0) : 0;
        return (
          <div key={i} className="flex items-center gap-2" data-testid={`misc-row-${i}`}>
            <Select value={r.material_id || undefined} onValueChange={(v) => setRow(i, "material_id", v)}>
              <SelectTrigger data-testid={`misc-mat-${i}`} className="h-8 text-xs rounded-lg flex-1"><SelectValue placeholder="Select item" /></SelectTrigger>
              <SelectContent>
                {opts.length ? opts.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name} · {money(o.sell_price)}/{o.unit}</SelectItem>
                )) : <div className="px-2 py-1.5 text-xs text-slate-400">No miscellaneous items for this section</div>}
              </SelectContent>
            </Select>
            <Input type="number" min={0} value={r.qty} onChange={(e) => setRow(i, "qty", e.target.value)} data-testid={`misc-qty-${i}`} className="h-8 w-20 rounded-lg num text-right" />
            <span className="text-xs num text-slate-500 w-16 text-right" data-testid={`misc-line-${i}`}>{money(line)}</span>
            <button type="button" onClick={() => rmRow(i)} data-testid={`misc-remove-${i}`} className="text-slate-400 hover:text-red-500"><X size={15} /></button>
          </div>
        );
      })}
      <Button type="button" size="sm" variant="outline" data-testid="misc-add" onClick={addRow} className="h-7 text-xs rounded-lg"><Plus size={13} className="mr-1" />Add item</Button>
    </div>
  );
}

// Reusable "Miscellaneous" add-on picker (per-quote). `toggle="switch"` shows a Switch instead of a checkbox.
export function MiscPicker({ module, subtype, value = [], onChange, toggle = "checkbox" }) {
  const opts = useMiscOptions(module, subtype);
  const on = (value || []).length > 0;
  const setOn = (checked) => onChange(checked ? [{ material_id: "", qty: 1 }] : []);
  return (
    <div className={toggle === "switch" ? "mt-4 border-t border-slate-100 pt-3" : "mt-4"} data-testid="misc-picker">
      {toggle === "switch" ? (
        <div className="flex items-center justify-between">
          <Label className="text-xs flex items-center gap-1">Miscellaneous<HelpHint side="right">Add extra chargeable items to this quote (e.g. hardware, packaging, special media). Pick the item and quantity &mdash; each line adds to the subtotal.</HelpHint></Label>
          <Switch checked={on} onCheckedChange={setOn} data-testid="misc-toggle" />
        </div>
      ) : (
        <span className="inline-flex items-center gap-1">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
            <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} data-testid="misc-toggle" />
            Miscellaneous
          </label>
          <HelpHint side="right">Add extra chargeable items to this quote (e.g. hardware, packaging, special media). Pick the item and quantity &mdash; each line adds to the subtotal.</HelpHint>
        </span>
      )}
      {on && <div className="mt-2"><MiscRows opts={opts} value={value} onChange={onChange} /></div>}
    </div>
  );
}
